import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Card, Table, Button, Modal, Form, Row, Col, Badge, Alert } from 'react-bootstrap';
import { fetchCategories, createCategory, updateCategory, deleteCategory } from '../../redux/slices/categoriesSlice';

const CategoryList = () => {
  const dispatch = useDispatch();
  const { categories, loading, error } = useSelector((state) => state.categories);
  
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3498DB',
  });

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        color: category.color || '#3498DB',
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        color: '#3498DB',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const categoryData = {
      name: formData.name,
      description: formData.description || undefined,
      color: formData.color || undefined,
    };

    if (editingCategory) {
      await dispatch(updateCategory({ id: editingCategory.id, data: categoryData }));
    } else {
      await dispatch(createCategory(categoryData));
    }
    
    handleCloseModal();
    dispatch(fetchCategories());
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta categoría?')) {
      await dispatch(deleteCategory(id));
      dispatch(fetchCategories());
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">Cargando...</div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2>Categorías</h2>
          <p className="text-muted">Gestiona las categorías para organizar tus gastos</p>
        </Col>
        <Col className="text-end">
          <Button variant="primary" onClick={() => handleOpenModal()}>
            Nueva Categoría
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Color</th>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Gastos</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    No hay categorías registradas. Crea tu primera categoría para comenzar.
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr key={category.id}>
                    <td>
                      <div
                        style={{
                          width: '30px',
                          height: '30px',
                          backgroundColor: category.color || '#3498DB',
                          borderRadius: '50%',
                          border: '2px solid #ddd',
                        }}
                      ></div>
                    </td>
                    <td><strong>{category.name}</strong></td>
                    <td>{category.description || '-'}</td>
                    <td>
                      <Badge bg="info">{category.expenses_count || 0}</Badge>
                    </td>
                    <td>
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => handleOpenModal(category)}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(category.id)}
                      >
                        Eliminar
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ej: Entretenimiento, Educación, Transporte"
                maxLength={100}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Descripción opcional de la categoría"
                maxLength={255}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Color</Form.Label>
              <div className="d-flex align-items-center">
                <Form.Control
                  type="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  style={{ width: '60px', height: '40px', marginRight: '10px' }}
                />
                <Form.Control
                  type="text"
                  value={formData.color}
                  onChange={handleChange}
                  name="color"
                  placeholder="#3498DB"
                  maxLength={7}
                  style={{ width: '120px' }}
                />
              </div>
              <Form.Text className="text-muted">
                Selecciona un color para identificar visualmente esta categoría
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              {editingCategory ? 'Actualizar' : 'Crear'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default CategoryList;
